import React, { useEffect, useState } from 'react';
import { Eye, Send, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

export const CollectionsDigestSettings = ({ api }) => {
  const [cfg, setCfg] = useState({ min_days_overdue: 1, min_overdue_amount: 0 });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api().get('/admin/collections-digest/settings').then(r => setCfg(r.data)).catch(() => {}); }, [api]);

  const save = async () => {
    try {
      const r = await api().put('/admin/collections-digest/settings', { min_days_overdue: Number(cfg.min_days_overdue) || 1, min_overdue_amount: Number(cfg.min_overdue_amount) || 0 });
      setCfg(r.data); toast.success('Digest thresholds saved');
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Save failed'); }
  };
  const openPreview = async () => {
    try { const r = await api().get('/admin/collections-digest/preview'); setPreview(r.data); }
    catch { toast.error('Preview failed'); }
  };
  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await api().post('/admin/collections-digest/send-test');
      if (r.data.status === 'sent' || r.data.status === 'ok') toast.success(`Digest sent (${r.data.customers} customers)`);
      else toast.error(`Not sent: ${r.data.detail || r.data.reason || r.data.status}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); } finally { setBusy(false); }
  };

  return (
    <Card className="industrial-card" data-testid="collections-digest-card">
      <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-lg">Collections Digest</CardTitle></CardHeader>
      <CardContent className="p-4 space-y-3">
        <p className="text-sm text-slate-500">Weekdays at 8:00 AM Mountain, all Admins (plus the "Collections Digest" list in Email Settings) get a list of customers with overdue balances. Skipped when nobody qualifies.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label>Min days overdue</Label><Input type="number" min="1" value={cfg.min_days_overdue} onChange={(e) => setCfg({ ...cfg, min_days_overdue: e.target.value })} className="w-36 rounded-sm" data-testid="digest-min-days" /></div>
          <div><Label>Min overdue amount ($)</Label><Input type="number" min="0" step="0.01" value={cfg.min_overdue_amount} onChange={(e) => setCfg({ ...cfg, min_overdue_amount: e.target.value })} className="w-40 rounded-sm" data-testid="digest-min-amount" /></div>
          <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="digest-save-btn"><Save className="w-4 h-4 mr-1" /> Save</Button>
          <Button variant="outline" onClick={openPreview} className="rounded-sm" data-testid="digest-preview-btn"><Eye className="w-4 h-4 mr-1" /> Preview</Button>
          <Button variant="outline" onClick={sendTest} disabled={busy} className="rounded-sm" data-testid="digest-send-test-btn"><Send className="w-4 h-4 mr-1" /> {busy ? 'Sending…' : 'Send now'}</Button>
        </div>
      </CardContent>
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="digest-preview-dialog">
          <DialogHeader><DialogTitle>Collections Digest Preview</DialogTitle></DialogHeader>
          {preview && (
            <>
              <div className="text-xs text-slate-600 space-y-0.5" data-testid="digest-preview-meta">
                <div><strong>{preview.customers.length}</strong> customer(s) · total overdue <strong>${preview.total_overdue.toFixed(2)}</strong></div>
                <div>Admins: {preview.recipients.join(', ') || 'none'} (+ Email Settings list)</div>
                {preview.last_run && <div>Last scheduled run: {new Date(preview.last_run.started_at).toLocaleString()} · {preview.last_run.status}{preview.last_run.detail ? ` · ${preview.last_run.detail}` : ''}</div>}
              </div>
              {preview.customers.length
                ? <iframe title="digest" srcDoc={preview.html} className="w-full h-[60vh] border border-slate-200 rounded-sm" data-testid="digest-preview-frame" />
                : <p className="text-sm text-slate-500 py-6 text-center">No customers meet the thresholds, so the scheduled email would be skipped.</p>}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CollectionsDigestSettings;
